import { FeatureFlag } from '@/constants/feature-flags.ts';
import { type AppointmentChainConfig } from '@/models/appointment-chain-config.ts';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useChainFooter } from '@/hooks/use-chain-footer.ts';
import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';

import { getText } from '@/utils/helpers.js';

import { useBookingStore, useSearchStore } from '@/store.js';

import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';
import UnderlinedButton from '../UnderlinedButton.jsx';
import ChainSuggestionEmpty from './chain-suggestion-empty.tsx';
import ChainSuggestionsTable from './chain-suggestions-table.tsx';

// Back/Continue live in the shared booking-modal navBar, not in this step.
const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    pagingRow: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 24,
        minHeight: 32,
    },
    spinnerArea: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 0',
    },
}));

type ChainMainPractitioner = { type: 'specific'; id: string } | { type: 'no_preference' } | null;
type PrismicText = { text: string }[] | undefined;
type Candidate = { display_name?: PrismicText } | null;

const ChainSuggestionsStep = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { value: isChainBookingEnabled } = useFeatureFlag(FeatureFlag.IS_CHAIN_BOOKING_ENABLED_FOR_PATIENTS);

    const {
        availability,
        isChainSuggestionFetching,
        hasPreviousSuggestion,
        runAutoSuggestion,
        handleNextSuggestion,
        handlePreviousSuggestion,
        handleBack,
    } = useChainFooter();

    const selectedChainConfig = useSearchStore(
        (state: any) => state.selectedChainConfig
    ) as AppointmentChainConfig | null;
    const treatments = useSearchStore((state: any) => state.treatments) as Record<
        string,
        Record<string, { name?: PrismicText }>
    > | null;
    const selectedLocation = useSearchStore((state: any) => state.selectedLocation) as string | null;
    const chainMainPractitioner = useSearchStore((state: any) => state.chainMainPractitioner) as ChainMainPractitioner;
    const chainMainPractitionerCandidate = useSearchStore(
        (state: any) => state.chainMainPractitionerCandidate
    ) as Candidate;

    // Ref + clean-slate guard: block StrictMode's double-mount and post-paging re-entry from re-firing the fetch.
    const hasRunAutoSuggestion = useRef(false);
    useEffect(() => {
        if (!isChainBookingEnabled) return;
        const isCleanSlate = availability.length === 0 && !hasPreviousSuggestion && !isChainSuggestionFetching;
        if (hasRunAutoSuggestion.current || !isCleanSlate) return;

        hasRunAutoSuggestion.current = true;
        runAutoSuggestion();
    }, [
        isChainBookingEnabled,
        availability.length,
        hasPreviousSuggestion,
        isChainSuggestionFetching,
        runAutoSuggestion,
    ]);

    // Defense-in-depth: entry points gate on the flag too, but guard here so no chain code runs if it's off.
    if (!isChainBookingEnabled) return null;

    const resolveStepLabel = (appointmentTypeKey: string): string =>
        (selectedLocation ? getText(treatments?.[selectedLocation]?.[appointmentTypeKey]?.name) : undefined) ??
        appointmentTypeKey;

    // Reuses the footer's stack-clearing handleBack, then closes to the practitioner screen (ET-5424).
    const handleChooseDifferent = (): void => {
        handleBack();
        useBookingStore.setState({ showBookingModal: false });
    };

    const hasSuggestions = availability.length > 0;
    const practitionerName = getText(chainMainPractitionerCandidate?.display_name) ?? '';

    const spinner = (
        <div className={classes.spinnerArea} data-testid="chain-loading-spinner">
            <EternoSpinner />
        </div>
    );

    if (hasSuggestions && selectedChainConfig) {
        return (
            <div className={classes.container} data-testid="chain-suggestions-step">
                {isChainSuggestionFetching ? (
                    spinner
                ) : (
                    <ChainSuggestionsTable
                        items={availability}
                        selectedChainConfig={selectedChainConfig}
                        resolveStepLabel={resolveStepLabel}
                    />
                )}

                <div className={classes.pagingRow}>
                    {hasPreviousSuggestion && (
                        <UnderlinedButton
                            noMarginTop
                            text={t('ChainPreviousSuggestion')}
                            disabled={isChainSuggestionFetching}
                            onPress={handlePreviousSuggestion}
                            dataTestId="chain-previous-suggestion-btn"
                        />
                    )}
                    <UnderlinedButton
                        noMarginTop
                        text={t('ChainNextSuggestion')}
                        disabled={isChainSuggestionFetching}
                        onPress={handleNextSuggestion}
                        dataTestId="chain-next-suggestion-btn"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container} data-testid="chain-suggestions-step">
            {isChainSuggestionFetching ? (
                spinner
            ) : (
                <ChainSuggestionEmpty
                    variant={chainMainPractitioner?.type === 'specific' ? 'specific' : 'no_preference'}
                    practitionerName={practitionerName}
                    onChooseDifferent={handleChooseDifferent}
                    hasPreviousSuggestion={hasPreviousSuggestion}
                    onPreviousSuggestion={handlePreviousSuggestion}
                />
            )}
        </div>
    );
};

export default ChainSuggestionsStep;
